import { Vue, Component, Watch, Prop } from 'vue-property-decorator';
import GoogleMaterialIcon from '@/components/icons/google-material-icon/google-material-icon';
import MCIcon from '@/components/icons/mc-icon/mc-icon';
import ActivitySearchFilter from './activity-search-filter/activity-search-filter';
import FilterCard from '@/components/cards/filter-card/filter-card';
import { CATALOG_MENUS, LIBRARY_TYPES, CONTENT_TYPES, CONTENT_MODEL } from '@/utils/constants';
import SubContentFilter from './sub-content-filter/sub-content-filter';
import { tree } from 'd3';
import { sessionService } from '@/providers/services/auth/session';
import { activeTab } from '@/utils/utils';
import ActivityContent from '@/components/cards/content-loader/activity-content/activity-content';

@Component({
  name: 'activity-search',
  components: {
    'material-icon': GoogleMaterialIcon,
    'mc-icon': MCIcon,
    'activity-search-filter': ActivitySearchFilter,
    'filter-card': FilterCard,
    'sub-filter': SubContentFilter,
  },
})

export default class ActivitySearch extends Vue {

  // --------------------------------------------------------------------------------
  // Properties

  @Prop()
  private filterParams!: any;

  @Prop()
  private parentChanged: any;

  private activityTabs: any = [];

  private get librariesList() {
    let activeList = LIBRARY_TYPES;
    if (this.$access.isCapLibrary) {
      activeList = LIBRARY_TYPES.filter((item) => item.key !== 'gooru_catalog');
      activeList[1].isActive = true;
    }
    return activeList;
  }

  private searchTerms: string = '';

  private isSearchTerms: boolean = false;

  private filterList: any = {};

  private activeActivity: any = null;

  private contentType: any = [];

  private isShowFilterDropdown: boolean = false;

  private activeLibrary: any = null;

  private resourceIngestJobId: any = '';

  private listCheckRest = ['category', 'subject', 'framework', 'domain'];

  private get isShareableLink() {
    return sessionService.getSession()!.isShareableLink;
  }

  @Watch('filterList', { deep: true })
  private onChangeFilterList(value: any) {
    if (this.$parent) {
      this.$parent.$data.filterParams = { q: this.searchTerms !== '' ? this.searchTerms : '*' };
      if (value.category) {
        this.$parent.$data.filterParams['flt.subjectClassification'] = value.category.id;
      }
      if (value.subject) {
        this.$parent.$data.filterParams['flt.subject'] = value.subject.id;
      }
      if (value.course) {
        this.$parent.$data.filterParams['flt.course'] = this.arrangeParams(value.course, ',', 'id');
      }
      if (value.licenses) {
        this.$parent.$data.filterParams['flt.licenseName'] = this.arrangeParams(value.licenses, '~~');
      }
      if (value['21-century-skills']) {
        this.$parent.$data.filterParams['flt.21CenturySkills'] =
          this.arrangeParams(value['21-century-skills'], '~~');
      }
      if (value.audience) {
        this.$parent.$data.filterParams['flt.audience'] = this.arrangeParams(value.audience);
      }
      if (value.dok) {
        this.$parent.$data.filterParams['flt.depthOfKnowledge'] = this.arrangeParams(value.dok);
      }
      if (value.domain) {
        this.$parent.$data.filterParams['flt.domain'] = value.domain.id;
      }
      if (value.standard) {
        this.$parent.$data.filterParams['flt.standard'] = value.standard.join(',');
      }
      if (value.framework) {
        this.$parent.$data.filterParams.fwCode = value.framework.standard_framework_id;
      }
      if (this.activeLibrary.scopeKey) {
        this.$parent.$data.filterParams.scopeKey = this.activeLibrary.scopeKey;
      }

      if (this.activeActivity && this.activeActivity.key === 'resource' && this.resourceIngestJobId) {
        this.$parent.$data.filterParams['flt.creatorSystem'] = 'Resource_Ingest_job_' + this.resourceIngestJobId;
        this.$parent.$data.filterParams['flt.publishStatus'] = 'unpublished';
        this.$parent.$data.filterParams.q = '*';
        this.$parent.$data.filterParams.scopeKey = null;
      }
    }
  }

  get hasActiveTab() {
    return this.activityTabs.find((tab: any) => tab.isActive && tab.isDisabled);
  }

  @Watch('parentChanged')
  private onChangeParentAction(value: any) {
    if (value) {
      this.selectTenantCard();
    }
  }

  // --------------------------------------------------------------------------------
  // Hooks

  private created() {
    this.activityTabs = CATALOG_MENUS;
    this.contentType = CONTENT_TYPES;
    this.onLoadInitial();
  }

  // ----------------------------------------------------------------------------------
  // Actions
  private onSearch() {
    if (this.searchTerms !== '') {
      this.isSearchTerms = true;
      this.$set(this.filterParams, 'q', this.searchTerms);
    }
  }

  private switchSearch() {
    this.$emit('switchSearch');
  }

  private clearSearchBox() {
    this.isSearchTerms = false;
    this.searchTerms = '';
    this.$set(this.filterParams, 'q', '*');
  }

  private onChangeTab(activity: any) {
    this.activeActivity = activity;
    if (this.activeActivity.key !== 'resource') {
      this.$set(this.filterParams, 'flt.creatorSystem', null);
      this.$set(this.filterParams, 'flt.publishStatus', null);
      this.onChangeFilterList(this.filterParams);
    }
    const activeTabs = this.activityTabs.find((item: any) => item.isActive);
    this.$set(activeTabs, 'isActive', false);
    if (!activeTabs.isActive) {
      this.$set(activity, 'isActive', true);
    }
    this.$emit('onChangeActiveTab', activity);
  }

  private onSelectCategory(category: any, filter: any) {
    if (this.listCheckRest.includes(filter.code)) {
      this.filterList = this.resetCheckbox(filter);
      if (!category.checked) {
        return;
      }
    }
    this.$set(this.filterList, filter.code, category);
  }

  private onClearItem(item: any) {
    item.checked = false;
    if (this.listCheckRest.includes(item.type)) {
      const activeList = this.resetCheckbox(item);
      this.$set(this, 'filterList', activeList);
    } else {
      item.checked = false;
      const activeList = (this.filterList[item.type]).filter((items: any) => items.checked);
      this.$set(this.filterList, item.type, activeList);
    }
  }

  private onSelectDropdown(libraries: any, tabIndex = 1) {
    this.activeLibrary = libraries;
    this.onChangeFilterList(this.filterList);
    let activeTabIndex = this.activityTabs.findIndex(
      (tab: any) => tab.isActive && !tab.isDisabled);
    activeTabIndex = activeTabIndex !== -1 ? activeTabIndex : tabIndex;
    activeTab(this.activityTabs, 'summary').isDisabled = false;
    if (libraries.key !== 'gooru_catalog') {
      activeTabIndex = !activeTabIndex ? 1 : activeTabIndex;
      activeTab(this.activityTabs, 'summary').isDisabled = true;
      if (libraries.key === 'libraries') {
        if (this.$access.isCapLibrary) {
          if (this.$parent) {
            this.$parent.$data.parentChanged = null;
          }
        }
        ActivityContent.previousTab = activeTabIndex;
        const libraryMenu = activeTab(this.activityTabs, 'partnerContent');
        this.onChangeTab(libraryMenu);
        return;
      }
    }
    if (!activeTab(this.activityTabs, 'summary').isDisabled && this.filterParams.q === '*') {
      activeTabIndex = 0;
    }
    this.onChangeTab(this.activityTabs[activeTabIndex]);
  }


  private onClickAdd(type: any = null) {
    const currentRoute = this.$router.currentRoute.path;
    if (type === 'offline-activity') {
      window.location.href = `/library/content/activity/edit/new?isFromOA=true&redirectUrl=${currentRoute}`;
    } else {
      if (type === 'bulk-upload') {
        window.location.href = `/catalog/my-content/bulk-upload?type=my-content`;
      } else if (type === 'request-upload') {
        window.location.href = `/catalog/my-content/request-upload?type=my-content`;
      } else if (type === 'rubric') {
        window.location.href = `/library/content/${type}/edit/new?redirectUrl=${currentRoute}`;
      } else {
        window.location.href = `/library/content/${type}s/edit/new?redirectUrl=${currentRoute}`;
      }
    }
  }

  // --------------------------------------------------------------
  // Methods

  private resetCheckbox(item: any) {
    let activeList = {};
    Object.keys(this.filterList).map((itemValue: any) => {
      if (Array.isArray(this.filterList[itemValue])) {
        if (itemValue === 'standard') {
          this.filterList[itemValue] = [];
        } else {
          this.filterList[itemValue].map((items: any) => {
            items.checked = false;
          });
        }
      } else {
        if ((item.type || item.code) === 'category') {
          if (this.filterList.subject) {
            this.filterList.subject.checked = false;
          }
          if (this.filterList.framework) {
            this.filterList.framework.checked = false;
          }
          if (this.filterList.domain) {
            this.filterList.domain.checked = false;
          }
          if (this.filterList.course) {
            this.filterList.course.checked = false;
          }
          if (this.filterList.standard) {
            this.filterList.standard = [];
          }
          activeList = {};
        } else if ((item.type || item.code) === 'subject') {
          activeList = { category: this.filterList.category };
          if (this.filterList.framework) {
            this.filterList.framework.checked = false;
          }
          if (this.filterList.domain) {
            this.filterList.domain.checked = false;
          }
          if (this.filterList.course) {
            this.filterList.course.checked = false;
          }
          if (this.filterList.standard) {
            this.filterList.standard = [];
          }
        } else if ((item.type || item.code) === 'framework') {
          if (this.filterList.course) {
            this.filterList.course.checked = false;
          }
          if (this.filterList.domain) {
            this.filterList.domain.checked = false;
          }
          if (this.filterList.standard) {
            this.filterList.standard = [];
          }
          activeList = {
            category: this.filterList.category,
            subject: this.filterList.subject,
          };
        } else if ((item.type || item.code) === 'course') {
          if (this.filterList.domain) {
            this.filterList.domain.checked = false;
          }
          if (this.filterList.standard) {
            this.filterList.standard = [];
          }
          activeList = {
            category: this.filterList.category,
            subject: this.filterList.subject,
            framework: this.filterList.framework,
          };
        } else if ((item.type || item.code) === 'domain') {
          activeList = {
            category: this.filterList.category,
            subject: this.filterList.subject,
            framework: this.filterList.framework,
            course: this.filterList.course,
          };
        }
      }
    });
    return activeList;
  }

  /**
   * Help to arrange params
   * @param filterInfo
   * @param delimiter
   * @param keyName
   */
  private arrangeParams(filterInfo: any, delimiter = ',', keyName = 'label') {
    let label = '';
    if (Array.isArray(filterInfo)) {
      filterInfo.map((filterData) => {
        label += delimiter + filterData[`${keyName}`];
      });
      const numOfCharsRemove = delimiter === ',' ? 1 : 2;
      return label.substring(numOfCharsRemove);
    }
    return label;
  }

  /**
   * Handled the logic when we have active tab in query params
   */
  private onLoadInitial() {
    const querParams = this.$route.query || {};
    this.activeLibrary = this.librariesList.find(
      (item: any) => querParams.type ? item.scopeKey === querParams.type : item.isActive);
    if (this.activeLibrary.key === 'gooru_catalog') {
      activeTab(this.activityTabs, 'summary').isDisabled = false;
      this.onSelectDropdown(this.activeLibrary, 1);
    }
    if (this.$access.isCapLibrary) {
      activeTab(this.activityTabs, 'summary').isDisabled = true;
      if (this.activeLibrary) {
        this.onSelectDropdown(this.activeLibrary, 1);
      }

    }

    // resource job filter
    if (querParams.type === 'my-content' && querParams.id !== null) {
      const libraryMenu = activeTab(this.activityTabs, 'resource');
      this.onChangeTab(libraryMenu);
      if (
        this.activeLibrary.key === 'my_content' &&
        libraryMenu.key === 'resource'
      ) {
        this.resourceIngestJobId = querParams.id;
      }
    }

    if (querParams.type) {
      let activeTabIndex = querParams.activeTab ? this.activityTabs.findIndex(
        (tab: any) => tab.key === querParams.activeTab) : 1;
      activeTabIndex = activeTabIndex !== -1 ? activeTabIndex : 1;
      if (this.activeLibrary) {
        this.onSelectDropdown(this.activeLibrary, activeTabIndex);
      }
      this.$router.replace('/catalog');
    }
  }

  private selectTenantCard() {
    const categoryList = this.$store.state.activityStore.categoryList;
    const activeItem = categoryList.find((item: any) => item.id === 'k_12');
    this.$set(activeItem, 'checked', true);
    this.$set(this.filterList, 'category', activeItem);
    this.$store.dispatch('activityStore/fetchSubject', activeItem.id);
  }

}
